/**
 * The stored field a reasoning-level read looks at.
 *
 * Optional and nullable because this is the shape of a JSON column written by any
 * version of the write path, not of a parsed config.
 */
export type StoredEffortLevels = {
  readonly reasoningLevel?: string | null;
};

/**
 * The level to send, read off a stored config: `reasoningLevel`, the one level
 * field every provider reads.
 *
 * Returns the value VERBATIM. It is deliberately not validated here and
 * deliberately not defaulted here:
 *
 * - **Not validated**, because a value cannot be validated without the model it
 *   is sent to. The vocabularies OVERLAP rather than partition: `anthropic-adaptive`
 *   and `openai-reasoning` accept the identical set and `kimi-effort`'s is a
 *   strict subset of both, so a stored value never says which dialect it was
 *   chosen for. `resolveModelTuning` (`models/model-controls.ts`) resolves the word
 *   against the model's catalog entry: a word the entry offers is sent as is, a
 *   word stored while the model spoke another dialect is carried to the offered
 *   word on the same rung, and a word with no counterpart falls back to the
 *   entry's default and is reported in `replaced`. Validating here would discard
 *   the values that step exists to rescue.
 * - **Not defaulted**, because the default is per model: each catalog entry
 *   declares its own `defaultEffort`, and `vendor-default` means no level is sent
 *   at all. `resolveModelTuning` applies it. Defaulting here would put one default
 *   on every model and start billing thinking on turns that send none today.
 */
export function storedEffortLevelOf(
  stored: StoredEffortLevels | null | undefined
): string | undefined {
  return stored?.reasoningLevel ?? undefined;
}
